import { Suspense, useState } from 'react';
import { useTracking } from 'react-tracking';

import loadable from '@loadable/component';
import { NarrativeFairValueActionSheet } from './components/NarrativeFairValueActionSheet';
import { ValuationSelectionForm } from './components/ValuationSelectionForm';
import {
  Alert,
  Text,
  AlertAction,
  Bones,
  Box,
  Grid,
} from '@simplywallst/ui-core';
import { ErrorBoundary } from '@sentry/react';
import { Header } from '@/components/ActionSheet';

const NarrativeOnboardingModal = loadable(
  () => import('@components/NarrativeOnboardingModal')
);

export interface Props {
  companyId: string;
  isModalOpen: boolean;
  onClose: () => void;
  userHoldsPosition?: boolean;
}

export const NarrativeFairValueModal = ({
  companyId,
  isModalOpen,
  onClose,
  userHoldsPosition,
}: Props) => {
  const [showingOnboarding, setShowingOnboarding] = useState(false);

  const { Track } = useTracking({
    type: 'track',
    subject: 'FairValue',
    companyId,
  });

  if (showingOnboarding)
    return (
      <Track>
        <NarrativeOnboardingModal
          companyId={companyId}
          isOpen={showingOnboarding}
          onClose={() => setShowingOnboarding(false)}
        />
      </Track>
    );

  return (
    <Track>
      <NarrativeFairValueActionSheet isOpen={isModalOpen} onClose={onClose}>
        <ErrorBoundary
          fallback={
            <Grid gap="16px">
              <Header>
                <Text typography="baseStrong" color="textSolid">
                  Valuation
                </Text>
              </Header>
              <Box paddingX="x3">
                <Alert
                  styleType="negative"
                  actions={
                    <AlertAction onClick={() => window.location.reload()}>
                      Reload
                    </AlertAction>
                  }
                >
                  Failed to load narratives and fair value. Please try again.
                </Alert>
              </Box>
            </Grid>
          }
        >
          <Suspense
            fallback={
              <Grid
                paddingXMd="x4"
                paddingX="x2"
                paddingTop="x3"
                rowGap="8px"
                autoRows="max-content"
                role="presentation"
                aria-label="Loading skeleton"
              >
                <Bones minHeight="40px" marginBottom="x3" />
                <Bones minHeight="90px" />
                <Bones minHeight="90px" />
                <Bones minHeight="90px" />
              </Grid>
            }
          >
            {isModalOpen && (
              <ValuationSelectionForm
                onClose={onClose}
                companyId={companyId}
                openOnboardingFlow={() => setShowingOnboarding(true)}
                userHoldsPosition={userHoldsPosition}
              />
            )}
          </Suspense>
        </ErrorBoundary>
      </NarrativeFairValueActionSheet>
    </Track>
  );
};
